import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUserDetails } from 'entities/user/model';
import { useAuth0 } from '@auth0/auth0-react';
import { TOKEN_TYPES, upperCaseNetworkType } from 'features/bill/lib';
import { isDev } from 'shared/lib/env';

export const useInitiateCrossChainTransfer = ({
  transferIds,
}: {
  transferIds: string[];
}) => {
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<
    {
      depositInfo: {
        token: string;
        chain: string;
        amount: string;
        fees: string;
        address: string;
      };
    },
    any,
    any,
    any
  >({
    queryKey: ['initiate-cross-chain-transfer'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.post<{
        result: {
          depositInfo: {
            token: string;
            chain: string;
            amount: string;
            fees: string;
            address: string;
          };
        };
      }>(
        `${process.env.REACT_APP_SERVER_URL}/api/public/cross-chain-transfer/asset-transfer/initiate`,
        {
          transferIds,
          providerId: 'easy_pay',
          organizationId: getCurrentOrganizationId(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // if (isDev()) {
      //   return {
      //     depositInfo: {
      //       amount: '0.001',
      //       token: TOKEN_TYPES['USDC.native_polygon'],
      //       chain: 'Polygon',
      //       fees: '1',
      //       address: '0x4baB3904C07933F190Eb9d8ffd17214aD703147f',
      //     },
      //   };

      //   // if (depositTokenChain === 'solana') {
      //   //   return {
      //   //     depositInfo: {
      //   //       amount: 0.001,
      //   //       token: 'USDC',
      //   //       chain: 'Solana',
      //   //       depositWallet: {
      //   //         address: '6eCGHoMYsG435eqPvPTZXCK6FFVzLNdBBYQ8rzw11hCp',
      //   //         // address: 'rk3qxNGa4smazGZaWCyTUWAsRKjdG64LNspviARnCAe', // Pavel's prod liquidation address
      //   //       },
      //   //     },
      //   //   };
      //   // }
      // }

      /** Since we hardcode deposit token for now, for our system we need explicitly say about polygon native
       * "token in our language" */
      if (
        res.data?.result &&
        res.data?.result.depositInfo.chain === 'polygon' &&
        res.data?.result?.depositInfo?.token === 'usdc'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_polygon'];
      } else if (
        res.data?.result &&
        res.data?.result.depositInfo.chain === 'arbitrum' &&
        res.data?.result?.depositInfo?.token === 'usdc'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_arbitrum'];
      } else {
        res.data.result.depositInfo.token =
          res.data.result.depositInfo.token?.toUpperCase();
      }

      res.data.result.depositInfo.chain = upperCaseNetworkType(
        res.data.result.depositInfo.chain
      );

      return res.data?.result;
    },
    enabled: !!transferIds.length && !!getCurrentOrganizationId(),
  });
};

export const useInitiateCrossChainTransferByInvoice = ({
  invoiceIds,
  sourceNetwork,
  sourceToken,
}: {
  invoiceIds: string[];
  sourceNetwork: string;
  sourceToken: string;
}) => {
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<
    {
      depositInfo: {
        token: string;
        chain: string;
        amount: string;
        fees: string;
        address: string;
      };
    },
    any,
    any,
    any
  >({
    queryKey: [
      'initiate-cross-chain-transfer-by-invoice',
      sourceNetwork,
      sourceToken,
      ...invoiceIds,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.post<{
        result: {
          depositInfo: {
            token: string;
            chain: string;
            amount: string;
            fees: string;
            address: string;
          };
        };
      }>(
        `${process.env.REACT_APP_SERVER_URL}/api/public/cross-chain-transfer/invoice/initiate`,
        {
          invoiceIds,
          sourceToken: sourceToken,
          sourceNetwork: sourceNetwork?.toLowerCase(),
          providerId: 'easy_pay',
          organizationId: getCurrentOrganizationId(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (isDev()) {
        return {
          depositInfo: {
            amount: '0.001',
            token: TOKEN_TYPES['USDC.native_polygon'],
            chain: 'Polygon',
            fees: '1',
            address: '0x4baB3904C07933F190Eb9d8ffd17214aD703147f',
          },
        };

        // if (sourceNetwork?.toLowerCase() === 'solana') {
        //   return {
        //     depositInfo: {
        //       amount: 0.001,
        //       token: 'USDC',
        //       chain: 'Solana',
        //       depositWallet: {
        //         address: '6eCGHoMYsG435eqPvPTZXCK6FFVzLNdBBYQ8rzw11hCp',
        //         // address: 'rk3qxNGa4smazGZaWCyTUWAsRKjdG64LNspviARnCAe', // Pavel's prod liquidation address
        //       },
        //     },
        //   };
        // }
      }

      /** Since we hardcode deposit token for now, for our system we need explicitly say about polygon native
       * "token in our language" */
      if (
        res.data?.result &&
        res.data?.result.depositInfo.chain === 'polygon' &&
        res.data?.result?.depositInfo?.token === 'usdc'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_polygon'];
      } else if (
        res.data?.result &&
        res.data?.result.depositInfo.chain === 'arbitrum' &&
        res.data?.result?.depositInfo?.token === 'usdc'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_arbitrum'];
      } else {
        res.data.result.depositInfo.token =
          res.data.result.depositInfo.token?.toUpperCase();
      }

      res.data.result.depositInfo.chain = upperCaseNetworkType(
        res.data.result.depositInfo.chain
      );

      return res.data?.result;
    },
    enabled:
      !!invoiceIds.length &&
      !!sourceNetwork &&
      !!sourceToken &&
      !!getCurrentOrganizationId(),
  });
};
